import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef } from 'react';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { SelectionToolbar } from '../interaction/SelectionToolbar';

/** Datos raw de shipment (PreInvoiceShipment) - misma estructura que InvoicesOrders */
interface ShipmentRow {
  id: number;
  [key: string]: unknown;
}

interface ViewPreInvoicesAgGridTableProps {
  /** Shipments raw de la API (misma estructura que InvoicesOrders) */
  rows: ShipmentRow[];
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
  selectedColumns?: string[];
  selectedViewPreInvoices?: Set<number>;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
  onSelectionChanged?: (selectedIds: Set<number>) => void;
  onEditPreInvoice?: (preInvoiceId: number) => void;
  onReturnPreInvoice?: (preInvoiceId: number) => void;
  onBlockPreInvoice?: (preInvoiceId: number) => void;
  onBulkInvoiceViewPreInvoices?: () => void;
  onBulkEditShipments?: () => void;
  showActions?: boolean;
}

export const ViewPreInvoicesAgGridTable = React.memo(function ViewPreInvoicesAgGridTable({
  rows,
  selectedColumns = [],
  selectedViewPreInvoices = new Set(),
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
  onSelectionChanged,
  onEditPreInvoice,
  onReturnPreInvoice,
  onBlockPreInvoice,
  onBulkEditShipments,
  showActions = true,
}: ViewPreInvoicesAgGridTableProps) {
  const gridRef = useRef<AgGridTableRef>(null);

  const { filteredColumnDefs } = useAgGridColumns({
    selectedColumns,
    onEditPreInvoice,
    onReturnPreInvoice,
    onBlockPreInvoice,
    showActions,
  });

  const handleSelectionChanged = useCallback(
    (selectedRows: ShipmentRow[]) => {
      const selectedIds = new Set(selectedRows.map((row) => row.id).filter(Boolean) as number[]);
      onSelectionChanged?.(selectedIds);
    },
    [onSelectionChanged],
  );

  const handleClearSelection = useCallback(() => {
    gridRef.current?.deselectAll();
    onSelectionChanged?.(new Set());
  }, [onSelectionChanged]);

  return (
    <div className="w-full relative flex flex-col flex-1 min-h-0">
      <div className="flex-1 min-h-0 flex flex-col">
        <AgGridTable
          ref={gridRef}
          data={rows}
          columnDefs={filteredColumnDefs as ColDef[]}
          height="100%"
          enableSelection={true}
          onSelectionChanged={handleSelectionChanged}
          onGridReady={() => undefined}
          className="rounded-lg shadow-sm min-h-0 flex-1"
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          gridOptions={{}}
          sorting={sorting}
          onSortingChange={onSortingChange}
          rowHeight={50}
        />
      </div>

      {/* Toolbar de selección */}
      <SelectionToolbar
        selectedCount={selectedViewPreInvoices.size}
        onClearSelection={handleClearSelection}
        onBulkEditShipments={onBulkEditShipments}
      />
    </div>
  );
});
